import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useQuery } from "react-query";

const CURRENT_MEMBER_PROFILE_FIELD_VALUES_KEY =
  "current-member-profile-field-values";

export type ProfileFieldValue = string | string[] | null;

interface ProfileFieldChoice {
  id: number;
  profile_field_choice: {
    id: number;
    value: string;
  };
}

interface CommunityMemberProfileField {
  community_member_choices: ProfileFieldChoice[];
  display_value: ProfileFieldValue;
  id: number;
  text: string | null;
  textarea: string | null;
}

interface ProfileFieldWithValue {
  community_member_profile_field: CommunityMemberProfileField | null;
  field_type: string;
  id: number;
  key: string;
  label: string;
}

interface ProfileFieldsResponse {
  not_visible: ProfileFieldWithValue[];
  visible: ProfileFieldWithValue[];
}

export const useCurrentMemberProfileFieldValues = () => {
  const { currentCommunityMember } = usePunditUserContext();

  const { data, isInitialLoading, isError } = useQuery<
    { profile_fields: ProfileFieldsResponse } | undefined,
    unknown,
    Record<number, ProfileFieldValue>
  >({
    queryKey: [
      CURRENT_MEMBER_PROFILE_FIELD_VALUES_KEY,
      currentCommunityMember?.public_uid,
    ],
    queryFn: async () =>
      reactQueryGet(
        internalApi.profile.show({
          memberPublicId: currentCommunityMember?.public_uid,
          params: { page_name: "edit_profile" },
        }),
      ),
    select: response => {
      if (!response?.profile_fields) {
        return {};
      }

      const allFields = [
        ...response.profile_fields.visible,
        ...response.profile_fields.not_visible,
      ];

      return allFields.reduce(
        (acc, field) => {
          if (field.community_member_profile_field) {
            acc[field.id] = field.community_member_profile_field.display_value;
          }
          return acc;
        },
        {} as Record<number, ProfileFieldValue>,
      );
    },
    enabled: Boolean(currentCommunityMember?.public_uid),
  });

  return {
    profileFieldValues: data ?? {},
    isLoading: isInitialLoading,
    isError,
  };
};
